/**
 * InspectionRounds Component
 * US-013D Phase 4: Mobile-Friendly Inspection Interface
 *
 * Features:
 * - Room-based batch checklist
 * - Large touch-friendly buttons (mobile optimised)
 * - Expected state per batch
 * - Quick contamination flagging
 * - Batch status updates
 */

import { useState, useCallback, useEffect, useMemo } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  Clock,
  Eye,
  Home,
  Loader2,
  RefreshCw,
  ShieldAlert,
  X
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { supabase } from '@/supabase-client'
import { toast } from 'sonner'
import { useRooms } from '@/hooks/useLocations'
import { usePhasePrediction } from '@/hooks/useProcessVariance'
import { LearningIndicator } from '@/components/production/variance'
import { parseDateString, toLocalDateString } from '@/utils/dateUtils'

const CONTAMINATION_TYPES = [
  { id: 'green_mold', name: 'Green Mold (Trichoderma)', severity: 'severe' },
  { id: 'black_mold', name: 'Black Mold', severity: 'severe' },
  { id: 'cobweb', name: 'Cobweb Mold', severity: 'moderate' },
  { id: 'bacterial', name: 'Bacterial (wet/sour)', severity: 'moderate' },
  { id: 'wet_spot', name: 'Wet Spot', severity: 'minor' },
  { id: 'lipstick', name: 'Lipstick Mold', severity: 'moderate' },
  { id: 'unknown', name: 'Unknown/Other', severity: 'moderate' }
]

const INSPECTION_ACTIONS = [
  { id: 'healthy', label: 'Healthy', icon: Check, color: 'bg-emerald-500 hover:bg-emerald-600' },
  { id: 'monitor', label: 'Monitor', icon: Eye, color: 'bg-amber-500 hover:bg-amber-600' },
  { id: 'contaminated', label: 'Contam', icon: ShieldAlert, color: 'bg-red-500 hover:bg-red-600' }
]

// =============================================================================
// Sub-components
// =============================================================================

/**
 * Map container status to process type for variance predictions
 */
const STATUS_TO_PROCESS_TYPE = {
  colonising: 'incubation',
  incubating: 'incubation',
  fruiting: 'fruiting',
  pinning: 'fruiting'
}

/**
 * Calculate days since a date
 */
function daysSince(dateString) {
  if (!dateString) return null
  const date = parseDateString(dateString)
  const today = new Date()
  const diffTime = today - date
  return Math.floor(diffTime / (1000 * 60 * 60 * 24))
}

/**
 * Expected timing indicator for containers
 * Shows predicted duration vs actual elapsed time
 */
function ExpectedTimingIndicator({ container }) {
  // Determine process type from container status
  const processType = STATUS_TO_PROCESS_TYPE[container.status]

  // Get strain ID for prediction lookup
  const strainId = container.strain_id

  // Fetch prediction (only if we have valid strain and process type)
  const { prediction, isLoading } = usePhasePrediction(
    processType ? strainId : null,
    processType
  )

  // Calculate days in current status based on status type
  const daysInStatus = useMemo(() => {
    // For fruiting/pinning containers, use actual_pin_date if available
    if ((container.status === 'fruiting' || container.status === 'pinning') && container.actual_pin_date) {
      return daysSince(container.actual_pin_date)
    }
    // For incubating containers, use creation_date (when the block was made)
    if (container.creation_date) {
      return daysSince(container.creation_date)
    }
    // Fall back to created_at
    return daysSince(container.created_at)
  }, [container.status, container.actual_pin_date, container.creation_date, container.created_at])

  // If no prediction available or not in a trackable status, don't show anything
  if (!processType || !strainId || !prediction || isLoading) {
    return null
  }

  const expected = prediction.prediction?.expected_days
  const pessimistic = prediction.prediction?.pessimistic_days
  const sampleCount = prediction.prediction?.sample_count || 0
  const isLearning = sampleCount < 10

  if (!expected || daysInStatus === null) {
    return null
  }

  // Determine schedule status
  let statusLabel = 'On Track'
  let statusClass = 'text-blue-600 bg-blue-50'

  if (daysInStatus > pessimistic) {
    statusLabel = 'Behind Schedule'
    statusClass = 'text-red-600 bg-red-50'
  } else if (daysInStatus > expected) {
    statusLabel = 'Nearing End'
    statusClass = 'text-amber-600 bg-amber-50'
  } else if (daysInStatus < expected * 0.6) {
    statusLabel = 'Early Stage'
    statusClass = 'text-emerald-600 bg-emerald-50'
  }

  return (
    <div className="mb-3 p-2 bg-slate-50 rounded border border-slate-100">
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-1.5">
          <Clock className="h-3 w-3 text-muted-foreground" />
          <span className="text-muted-foreground">
            Day {daysInStatus} of {expected.toFixed(0)}-{pessimistic?.toFixed(0) || '?'} expected
          </span>
          {isLearning && (
            <LearningIndicator sampleCount={sampleCount} minSamples={10} className="h-3 w-3" />
          )}
        </div>
        <span className={cn('px-1.5 py-0.5 rounded font-medium', statusClass)}>
          {statusLabel}
        </span>
      </div>
    </div>
  )
}

/**
 * Room selector for navigation
 */
function RoomSelector({ rooms, selectedRoom, onSelectRoom }) {
  return (
    <div className="flex overflow-x-auto gap-2 pb-2 -mx-4 px-4 md:mx-0 md:px-0 md:flex-wrap">
      {rooms.map((room) => (
        <Button
          key={room.id}
          variant={selectedRoom?.id === room.id ? 'default' : 'outline'}
          size="sm"
          className="flex-shrink-0"
          onClick={() => onSelectRoom(room)}
        >
          <Home className="h-4 w-4 mr-2" />
          {room.name}
        </Button>
      ))}
    </div>
  )
}

/**
 * Single container inspection card - large touch targets
 */
function ContainerInspectionCard({
  container,
  expectedStatus,
  onAction,
  loading,
  inspectionStatus
}) {
  const isContaminated = container.status === 'contaminated'
  const statusMismatch = container.status !== expectedStatus && !isContaminated

  const getStatusBadgeStyle = () => {
    if (isContaminated) return 'bg-red-100 text-red-700'
    if (statusMismatch) return 'bg-amber-100 text-amber-700'
    return 'bg-emerald-100 text-emerald-700'
  }

  const getInspectionIndicator = () => {
    if (!inspectionStatus) return null

    switch (inspectionStatus) {
      case 'healthy':
        return <div className="absolute top-2 right-2 w-3 h-3 rounded-full bg-emerald-500" />
      case 'monitor':
        return <div className="absolute top-2 right-2 w-3 h-3 rounded-full bg-amber-500" />
      case 'contaminated':
        return <div className="absolute top-2 right-2 w-3 h-3 rounded-full bg-red-500" />
      default:
        return null
    }
  }

  return (
    <div
      className={cn(
        'relative p-4 rounded-lg border transition-colors',
        isContaminated ? 'bg-red-50 border-red-200' :
        statusMismatch ? 'bg-amber-50 border-amber-200' :
        'bg-background border-border'
      )}
    >
      {getInspectionIndicator()}

      {/* Container Info */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <span className="font-bold text-lg">{container.code}</span>
          <Badge className={getStatusBadgeStyle()}>
            {container.status}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          {container.strains?.name || 'Unknown Strain'}
        </p>
        {container.position && (
          <p className="text-xs text-muted-foreground">
            Position: {container.position}
          </p>
        )}
      </div>

      {/* Expected Timing Indicator (variance predictions) */}
      <ExpectedTimingIndicator container={container} />

      {/* Status Mismatch Warning */}
      {statusMismatch && (
        <div className="mb-3 p-2 bg-amber-100 rounded text-xs text-amber-700">
          <AlertTriangle className="h-3 w-3 inline mr-1" />
          Expected: {expectedStatus}, Actual: {container.status}
        </div>
      )}

      {/* Action Buttons - Large Touch Targets */}
      <div className="grid grid-cols-3 gap-2">
        {INSPECTION_ACTIONS.map((action) => (
          <Button
            key={action.id}
            variant="ghost"
            disabled={loading || isContaminated}
            className={cn(
              'h-14 flex flex-col items-center justify-center text-white',
              action.color,
              inspectionStatus === action.id && 'ring-2 ring-offset-2 ring-primary'
            )}
            onClick={() => onAction(container.id, action.id)}
          >
            <action.icon className="h-5 w-5 mb-1" />
            <span className="text-xs">{action.label}</span>
          </Button>
        ))}
      </div>
    </div>
  )
}

/**
 * Contamination reporting dialog
 */
function ContaminationDialog({
  open,
  onClose,
  container,
  onSubmit,
  loading
}) {
  const [type, setType] = useState('')
  const [notes, setNotes] = useState('')

  const handleSubmit = () => {
    if (!type) return
    onSubmit(container.id, type, notes)
  }

  useEffect(() => {
    if (!open) {
      setType('')
      setNotes('')
    }
  }, [open])

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <ShieldAlert className="h-5 w-5" />
            Report Contamination
          </DialogTitle>
          <DialogDescription>
            Container: {container?.code}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm font-medium mb-2 block">
              Contamination Type
            </label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="h-12">
                <SelectValue placeholder="Select contamination type" />
              </SelectTrigger>
              <SelectContent>
                {CONTAMINATION_TYPES.map((ct) => (
                  <SelectItem key={ct.id} value={ct.id}>
                    <span className="flex items-center gap-2">
                      {ct.name}
                      <Badge variant="outline" className="text-xs">
                        {ct.severity}
                      </Badge>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">
              Notes (optional)
            </label>
            <Textarea
              placeholder="Describe the contamination location, extent, etc."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={!type || loading}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <ShieldAlert className="h-4 w-4 mr-2" />
            )}
            Report Contamination
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Progress summary bar
 */
function InspectionProgress({ containers, inspectionStatuses }) {
  const total = containers.length
  const inspected = Object.keys(inspectionStatuses).length
  const healthy = Object.values(inspectionStatuses).filter(s => s === 'healthy').length
  const monitoring = Object.values(inspectionStatuses).filter(s => s === 'monitor').length
  const contaminated = Object.values(inspectionStatuses).filter(s => s === 'contaminated').length

  const percentage = total > 0 ? (inspected / total) * 100 : 0

  return (
    <div className="p-4 bg-muted rounded-lg space-y-3">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">Inspection Progress</span>
        <span className="text-muted-foreground">
          {inspected} / {total} ({percentage.toFixed(0)}%)
        </span>
      </div>

      <div className="h-2 bg-background rounded-full overflow-hidden">
        <div
          className="h-full bg-primary transition-all"
          style={{ width: `${percentage}%` }}
        />
      </div>

      <div className="flex items-center justify-center gap-6 text-xs">
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
          <span>Healthy: {healthy}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
          <span>Monitor: {monitoring}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
          <span>Contam: {contaminated}</span>
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export default function InspectionRounds() {
  // Fetch rooms from database
  const { rooms, loading: roomsLoading, error: roomsError } = useRooms()

  const [selectedRoom, setSelectedRoom] = useState(null)
  const [containers, setContainers] = useState([])
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [inspectionStatuses, setInspectionStatuses] = useState({})
  const [contamDialog, setContamDialog] = useState({ open: false, container: null })

  /**
   * Load containers for selected room
   */
  const loadContainers = useCallback(async () => {
    if (!selectedRoom) return

    setLoading(true)

    try {
      const { data, error } = await supabase
        .from('containers')
        .select(`
          id,
          code,
          status,
          position,
          location,
          strain_id,
          creation_date,
          created_at,
          actual_pin_date,
          strains!containers_strain_id_fkey (name)
        `)
        .eq('location', selectedRoom.id)
        .not('status', 'in', '("finished","discarded")')
        .order('position', { ascending: true })

      if (error) throw error

      setContainers(data || [])
      // Reset inspection statuses for new room
      setInspectionStatuses({})
    } catch (err) {
      console.error('[InspectionRounds] Load error:', err)
      toast.error('Failed to load containers')
    } finally {
      setLoading(false)
    }
  }, [selectedRoom])

  // Load containers when room changes
  useEffect(() => {
    if (selectedRoom) {
      loadContainers()
    }
  }, [selectedRoom, loadContainers])

  /**
   * Handle inspection action
   */
  const handleInspectionAction = useCallback(async (containerId, action) => {
    // If contaminated, open dialog
    if (action === 'contaminated') {
      const container = containers.find(c => c.id === containerId)
      setContamDialog({ open: true, container })
      return
    }

    // For healthy/monitor, just update local state
    setInspectionStatuses(prev => ({
      ...prev,
      [containerId]: action
    }))

    toast.success(action === 'healthy' ? 'Marked Healthy' : 'Flagged for Monitoring')
  }, [containers])

  /**
   * Submit contamination report
   */
  const handleContaminationReport = useCallback(async (containerId, type, notes) => {
    setActionLoading(true)

    try {
      // Update container status
      const { error: containerError } = await supabase
        .from('containers')
        .update({
          status: 'contaminated',
          contamination_date: toLocalDateString(new Date()),
          contamination_type: type
        })
        .eq('id', containerId)

      if (containerError) throw containerError

      // Create contamination event
      const { error: eventError } = await supabase
        .from('contamination_events')
        .insert({
          reference_type: 'container',
          container_id: containerId,
          detected_date: toLocalDateString(new Date()),
          contamination_type: type,
          severity: CONTAMINATION_TYPES.find(ct => ct.id === type)?.severity || 'moderate',
          action_taken: 'quarantine',
          quarantine_start_date: toLocalDateString(new Date()),
          max_quarantine_date: toLocalDateString(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)),
          notes,
          detected_by: 'inspection_round'
        })

      if (eventError) throw eventError

      // Update local state
      setInspectionStatuses(prev => ({
        ...prev,
        [containerId]: 'contaminated'
      }))

      // Refresh containers
      await loadContainers()

      toast.warning('Contamination Reported - Container has been flagged and quarantined')

      setContamDialog({ open: false, container: null })
    } catch (err) {
      console.error('[InspectionRounds] Contamination report error:', err)
      toast.error('Failed to report contamination')
    } finally {
      setActionLoading(false)
    }
  }, [loadContainers])

  /**
   * Complete inspection round
   */
  const handleCompleteRound = useCallback(async () => {
    const inspectedCount = Object.keys(inspectionStatuses).length
    const totalCount = containers.length

    if (inspectedCount < totalCount) {
      toast.error(`Incomplete Inspection - ${totalCount - inspectedCount} containers not yet inspected`)
      return
    }

    toast.success(`Inspection Complete - ${selectedRoom.name} inspection round completed`)

    // Clear for next room
    setSelectedRoom(null)
    setContainers([])
    setInspectionStatuses({})
  }, [containers, inspectionStatuses, selectedRoom])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-primary" />
            Inspection Rounds
          </h2>
          <p className="text-sm text-muted-foreground">
            Daily container health checks
          </p>
        </div>
        {selectedRoom && (
          <Button variant="outline" size="sm" onClick={loadContainers} disabled={loading}>
            <RefreshCw className={cn('h-4 w-4 mr-2', loading && 'animate-spin')} />
            Refresh
          </Button>
        )}
      </div>

      {/* Room Selector */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Select Room</CardTitle>
        </CardHeader>
        <CardContent>
          {roomsLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Loading rooms...</span>
            </div>
          ) : roomsError ? (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Error loading rooms</AlertTitle>
              <AlertDescription>{roomsError}</AlertDescription>
            </Alert>
          ) : rooms.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground">
              No rooms configured. Add rooms in Settings.
            </div>
          ) : (
            <RoomSelector
              rooms={rooms}
              selectedRoom={selectedRoom}
              onSelectRoom={setSelectedRoom}
            />
          )}
        </CardContent>
      </Card>

      {/* No Room Selected */}
      {!selectedRoom && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Home className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">
              Select a room to begin inspection round
            </p>
          </CardContent>
        </Card>
      )}

      {/* Room Inspection */}
      {selectedRoom && (
        <>
          {/* Progress */}
          <InspectionProgress
            containers={containers}
            inspectionStatuses={inspectionStatuses}
          />

          {/* Container Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : containers.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <CheckCircle2 className="h-12 w-12 text-emerald-500 mb-4" />
                <p className="text-muted-foreground">
                  No active containers in {selectedRoom.name}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {containers.map((container) => (
                <ContainerInspectionCard
                  key={container.id}
                  container={container}
                  expectedStatus={selectedRoom.expectedStatus}
                  onAction={handleInspectionAction}
                  loading={actionLoading}
                  inspectionStatus={inspectionStatuses[container.id]}
                />
              ))}
            </div>
          )}

          {/* Complete Button */}
          {containers.length > 0 && (
            <div className="flex justify-end">
              <Button
                size="lg"
                onClick={handleCompleteRound}
                disabled={Object.keys(inspectionStatuses).length < containers.length}
              >
                <CheckCircle2 className="h-5 w-5 mr-2" />
                Complete Inspection Round
              </Button>
            </div>
          )}
        </>
      )}

      {/* Contamination Dialog */}
      <ContaminationDialog
        open={contamDialog.open}
        onClose={() => setContamDialog({ open: false, container: null })}
        container={contamDialog.container}
        onSubmit={handleContaminationReport}
        loading={actionLoading}
      />
    </div>
  )
}
